import { z } from 'zod';
import { MerchantTimeZoneSchema } from '../../timezone.utils';
import {
    TerminalCommandReadinessRequestOperation,
    TerminalCommandReadinessResourceKind,
    TerminalCommandReadinessStatus,
    TerminalCommandReadinessVersion,
    type TerminalCommandReadinessDetailsV1,
    type TerminalCommandReadinessPageV1,
    type TerminalCommandReadinessRequestV1,
    type TerminalCommandReadinessResourceSummaryV1,
    type TerminalCommandReadinessResultV1,
    type TerminalCommandReadinessInventoryObservationV1,
} from '../../types/ipg/terminal-command-readiness.types';

const TerminalCommandReadinessResourceKindSchema = z.enum(TerminalCommandReadinessResourceKind);

const TerminalCommandReadinessOneResourceRequestV1Schema = z
    .object({
        terminal_command_readiness_version: z.literal(TerminalCommandReadinessVersion),
        operation: z.literal(TerminalCommandReadinessRequestOperation.ONE_RESOURCE),
        resource_kind: TerminalCommandReadinessResourceKindSchema,
        resource_uuid: z.string().uuid(),
    })
    .strict();

const TerminalCommandReadinessChecklistPageRequestV1Schema = z
    .object({
        terminal_command_readiness_version: z.literal(TerminalCommandReadinessVersion),
        operation: z.literal(TerminalCommandReadinessRequestOperation.CHECKLIST_PAGE),
        organization_uuid: z.string().uuid(),
        resource_kind: TerminalCommandReadinessResourceKindSchema,
        cursor: z.string().trim().min(1).optional(),
        limit: z.number().int().min(1).max(50).optional(),
    })
    .strict();

export const TerminalCommandReadinessRequestV1Schema: z.ZodType<TerminalCommandReadinessRequestV1> =
    z.discriminatedUnion('operation', [
        TerminalCommandReadinessOneResourceRequestV1Schema,
        TerminalCommandReadinessChecklistPageRequestV1Schema,
    ]);

const TerminalCommandReadinessResourceSummaryBaseV1Schema = z.object({
    resource_uuid: z.string().uuid(),
    display_name: z.string().trim().min(1),
    ipg_id: z.union([z.literal(1), z.literal(2), z.literal(3)]),
});

export const TerminalCommandReadinessResourceSummaryV1Schema: z.ZodType<TerminalCommandReadinessResourceSummaryV1> =
    z.discriminatedUnion('resource_kind', [
        TerminalCommandReadinessResourceSummaryBaseV1Schema.extend({
            resource_kind: z.literal(TerminalCommandReadinessResourceKind.LOGICAL_TERMINAL),
        }).strict(),
        TerminalCommandReadinessResourceSummaryBaseV1Schema.extend({
            resource_kind: z.literal(TerminalCommandReadinessResourceKind.TERMINAL_DEVICE),
            is_virtual: z.boolean(),
        }).strict(),
    ]);

const TerminalCommandReadinessInventoryObservationV1Schema: z.ZodType<TerminalCommandReadinessInventoryObservationV1> =
    z.discriminatedUnion('found', [
        z.object({ found: z.literal(false) }).strict(),
        z.object({ found: z.literal(true), online: z.boolean() }).strict(),
    ]);

export const TerminalCommandReadinessDetailsV1Schema: z.ZodType<TerminalCommandReadinessDetailsV1> =
    z.discriminatedUnion('ipg_id', [
        z
            .object({
                ipg_id: z.literal(1),
                single_mid_inventory: TerminalCommandReadinessInventoryObservationV1Schema,
                multi_mid_inventory: TerminalCommandReadinessInventoryObservationV1Schema,
            })
            .strict(),
        z.object({ ipg_id: z.literal(2) }).strict(),
        z.object({ ipg_id: z.literal(3) }).strict(),
    ]);

export const TerminalCommandReadinessResultV1Schema: z.ZodType<TerminalCommandReadinessResultV1> = z
    .object({
        terminal_command_readiness_version: z.literal(TerminalCommandReadinessVersion),
        resource: TerminalCommandReadinessResourceSummaryV1Schema,
        status: z.enum(TerminalCommandReadinessStatus),
        message: z.string().trim().min(1),
        observed_at: z.iso.datetime({ offset: true }),
        merchant_timezone: MerchantTimeZoneSchema,
        details: TerminalCommandReadinessDetailsV1Schema,
    })
    .strict()
    .superRefine((result, context) => {
        if (result.resource.ipg_id !== result.details.ipg_id) {
            context.addIssue({
                code: 'custom',
                path: ['details', 'ipg_id'],
                message: 'Provider details must match the resource payment processor.',
            });
        }
    });

export const TerminalCommandReadinessPageV1Schema: z.ZodType<TerminalCommandReadinessPageV1> = z
    .object({
        terminal_command_readiness_version: z.literal(TerminalCommandReadinessVersion),
        results: z.array(TerminalCommandReadinessResultV1Schema),
        pagination: z
            .object({
                has_more: z.boolean(),
                next_cursor: z.string().trim().min(1).optional(),
            })
            .strict(),
    })
    .strict();

export const TerminalCommandReadinessRequestContract = {
    variants: {
        v1: {
            normalize: (request: TerminalCommandReadinessRequestV1) =>
                TerminalCommandReadinessRequestV1Schema.parse(request),
        },
    },
    writerVariant: 'v1',
} as const;

export const TerminalCommandReadinessResultContract = {
    variants: {
        v1: {
            normalize: (result: TerminalCommandReadinessResultV1) =>
                TerminalCommandReadinessResultV1Schema.parse(result),
        },
    },
    writerVariant: 'v1',
} as const;

export const TerminalCommandReadinessPageContract = {
    variants: {
        v1: {
            normalize: (page: TerminalCommandReadinessPageV1) =>
                TerminalCommandReadinessPageV1Schema.parse(page),
        },
    },
    writerVariant: 'v1',
} as const;
